import React, { useMemo } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Controller, useFieldArray, useForm } from 'react-hook-form';

import FlexContainer from 'client/app/apps/disco-prototype/components/FlexContainer';
import {
  Dataset,
  Liquidset,
  Plate,
} from 'client/app/apps/disco-prototype/dataStore/dexieStore';

type FieldKey = Exclude<keyof Liquidset, 'id' | 'name' | 'table'>;
type Field = {
  name: FieldKey;
  label: string;
};

const tableFields: Field[] = [
  { name: 'liquidName', label: 'Liquid Name' },
  { name: 'plateName', label: 'Plate Name' },
  { name: 'well', label: 'Well' },
];

type Props = {
  datasets: Dataset[];
  plates: Plate[];
  onSubmit: (v: Omit<Liquidset, 'id'>) => void;
};

export const DefineLiquidSetForm = ({ datasets, plates, onSubmit }: Props) => {
  const { control, register, resetField, handleSubmit, formState, watch } =
    useForm<Liquidset>({
      mode: 'onChange',
      defaultValues: {
        plate: '',
        name: '',
        liquidName: '',
        plateName: '',
        well: '',
        table: '',
        composition: [],
      },
    });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'composition',
  });

  const tableId = watch('table');
  const composition = watch('composition');

  const columns = useMemo(() => {
    if (tableId) {
      const dataset = datasets.find(ds => ds.id === tableId)!;
      return Object.keys(dataset.rows[0]);
    }
    return [];
  }, [datasets, tableId]);

  return (
    <FlexContainer
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      direction="column"
      scroll="auto"
      sx={{ gap: 2 }}
    >
      <Controller
        name="table"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <FormControl fullWidth margin="normal">
            <InputLabel>Table</InputLabel>
            <Select label="table" {...field}>
              {datasets.map(dataset => (
                <MenuItem key={dataset.id} value={dataset.id}>
                  {dataset.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      />
      <Controller
        name="plate"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <FormControl fullWidth margin="normal">
            <InputLabel>Plate</InputLabel>
            <Select label="Plate" {...field}>
              {plates.map(plate => (
                <MenuItem key={plate.id} value={plate.id}>
                  {plate.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      />

      <TextField
        {...register('name', { required: true })}
        label="Name"
        fullWidth
        margin="normal"
      />

      {tableFields.map(({ name, label }) => (
        <Controller
          key={name}
          name={name}
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <FormControl fullWidth margin="normal">
              <InputLabel>{label}</InputLabel>
              <Select label={label} {...field}>
                {columns.map(col => (
                  <MenuItem key={col} value={col}>
                    {col}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      ))}

      <Box>
        <Typography variant="h6">Composition</Typography>

        {fields.map((field, index) => (
          <Box
            key={field.id}
            sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 2 }}
          >
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Controller
                    name={`composition.${index}.nameIsConstant`}
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        {...field}
                        checked={field.value}
                        onChange={e => {
                          field.onChange(e);
                          resetField(`composition.${index}.name`);
                        }}
                      />
                    )}
                  />
                }
                label="Use constant value"
              />

              {composition[index].nameIsConstant ? (
                <TextField
                  fullWidth
                  margin="normal"
                  label="Name"
                  {...register(`composition.${index}.name`)}
                />
              ) : (
                <FormControl fullWidth margin="normal">
                  <InputLabel>Name</InputLabel>
                  <Controller
                    name={`composition.${index}.name`}
                    control={control}
                    render={({ field }) => (
                      <Select label="Name" {...field}>
                        {columns.map(col => (
                          <MenuItem key={col} value={col}>
                            {col}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              )}
            </Box>

            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Controller
                    name={`composition.${index}.concentrationIsConstant`}
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        {...field}
                        checked={field.value}
                        onChange={e => {
                          field.onChange(e);
                          resetField(`composition.${index}.concentration`);
                        }}
                      />
                    )}
                  />
                }
                label="Use constant value"
              />

              {composition[index].concentrationIsConstant ? (
                <TextField
                  fullWidth
                  margin="normal"
                  label="Concentration"
                  {...register(`composition.${index}.concentration`)}
                />
              ) : (
                <FormControl fullWidth margin="normal">
                  <InputLabel>Concentration</InputLabel>
                  <Controller
                    name={`composition.${index}.concentration`}
                    control={control}
                    render={({ field }) => (
                      <Select label="Concentration" {...field}>
                        {columns.map(col => (
                          <MenuItem key={col} value={col}>
                            {col}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              )}
            </Box>

            <IconButton onClick={() => remove(index)}>
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}

        <Button
          variant="outlined"
          onClick={() =>
            append({
              name: '',
              nameIsConstant: false,
              concentration: '',
              concentrationIsConstant: false,
            })
          }
          sx={{ mt: 2 }}
        >
          Add Composition
        </Button>
      </Box>

      <Button
        variant="contained"
        type="submit"
        color="secondary"
        disabled={!formState.isValid}
      >
        Add
      </Button>
    </FlexContainer>
  );
};
